import { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField as MuiTextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEnqueueSnackbar } from 'hooks';
import type { PackEntry } from 'types/shared';
import {
  buildPackEntry,
  defaultSplitPackCount,
  getTestCreativesIdToHypothesis,
  groupIdsByHypothesis,
  isValidSplitPackCountInput,
  packTagForHypothesis,
  parseSplitPackCountInput,
  splitIdsIntoPacks,
} from '../utils/hypothesisPackUtils';

interface SplittableHypothesisGroup {
  hypothesisLabel: string;
  creativeIds: string[];
  packCountInput: string;
}

const SplitInPacksButton = () => {
  const { onShowAlert } = useEnqueueSnackbar();
  const { watch, setValue, trigger } = useFormContext();
  const selectedCreatives = watch('selectedCreatives') as string[];
  const packsWithCreatives = watch('packsWithCreatives') as PackEntry[];
  const testCreativesTab = watch('testCreativesTab') as string | undefined;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [splitGroups, setSplitGroups] = useState<SplittableHypothesisGroup[]>([]);

  const packedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const pack of packsWithCreatives) {
      for (const id of pack.creatives) if (id !== undefined) ids.add(id);
    }
    return ids;
  }, [packsWithCreatives]);

  const unpackedSelectedIds = useMemo(
    () => selectedCreatives.filter((id) => !packedIds.has(id)),
    [selectedCreatives, packedIds],
  );

  const splittableByHypothesis = useMemo(() => {
    const idToHypothesis = getTestCreativesIdToHypothesis();
    const grouped = groupIdsByHypothesis(unpackedSelectedIds, idToHypothesis);
    return [...grouped.entries()].filter(([, ids]) => ids.length >= 2);
  }, [unpackedSelectedIds]);

  const isSplitAvailable =
    testCreativesTab === 'test_creatives' && splittableByHypothesis.length > 0;

  const totalSplittableCount = splittableByHypothesis.reduce(
    (sum, [, ids]) => sum + ids.length,
    0,
  );

  const handleOpenDialog = useCallback(() => {
    setSplitGroups(
      splittableByHypothesis.map(([hypothesisLabel, creativeIds]) => ({
        hypothesisLabel,
        creativeIds,
        packCountInput: String(defaultSplitPackCount(creativeIds.length)),
      })),
    );
    setDialogOpen(true);
  }, [splittableByHypothesis]);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setSplitGroups([]);
  }, []);

  const handlePackCountChange = useCallback(
    (hypothesisLabel: string, rawValue: string) => {
      if (rawValue !== '' && !/^\d+$/.test(rawValue)) return;
      setSplitGroups((prev) =>
        prev.map((group) =>
          group.hypothesisLabel === hypothesisLabel
            ? { ...group, packCountInput: rawValue }
            : group,
        ),
      );
    },
    [],
  );

  const handlePackCountBlur = useCallback((hypothesisLabel: string) => {
    setSplitGroups((prev) =>
      prev.map((group) => {
        if (group.hypothesisLabel !== hypothesisLabel) return group;
        const packCount = parseSplitPackCountInput(
          group.packCountInput,
          group.creativeIds.length,
        );
        return { ...group, packCountInput: String(packCount) };
      }),
    );
  }, []);

  const isConfirmDisabled = splitGroups.some(
    (group) =>
      !isValidSplitPackCountInput(
        group.packCountInput,
        group.creativeIds.length,
      ),
  );

  const handleConfirmSplit = useCallback(() => {
    const newPacks: PackEntry[] = [];

    for (const group of splitGroups) {
      const packCount = parseSplitPackCountInput(
        group.packCountInput,
        group.creativeIds.length,
      );
      const chunks = splitIdsIntoPacks(group.creativeIds, packCount);
      const tag = packTagForHypothesis(group.hypothesisLabel);
      for (const creatives of chunks) {
        if (creatives.length > 0) {
          newPacks.push(buildPackEntry(tag, creatives));
        }
      }
    }

    if (!newPacks.length) {
      handleCloseDialog();
      return;
    }

    const updatedPacks = [...packsWithCreatives, ...newPacks];
    setValue('packsWithCreatives', updatedPacks, { shouldValidate: true });
    setValue('activePacksWithCreatives', packsWithCreatives.length);
    setValue('selectedCreatives', [], { shouldValidate: true });

    onShowAlert(
      newPacks.length === 1 ? 'Pack added' : `${newPacks.length} packs added`,
      { delayAlert: 3500 },
    );

    void trigger('packsWithCreatives');
    handleCloseDialog();
  }, [
    splitGroups,
    packsWithCreatives,
    setValue,
    trigger,
    onShowAlert,
    handleCloseDialog,
  ]);

  return (
    <>
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        PaperProps={{ sx: { maxWidth: 440, width: '90%' } }}
      >
        <DialogTitle sx={{mb: 0, pb: 0}}>
          {`Split "${String(totalSplittableCount)}" creatives in packs`}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0 }}>
          {splitGroups.map((group) => (
            <Box key={group.hypothesisLabel}>
              <Typography variant="body2" fontWeight={400} sx={{ mb: 3 }}>
                {group.hypothesisLabel}
              </Typography>
              <MuiTextField
                type="text"
                inputMode="numeric"
                label="Split in"
                value={group.packCountInput}
                onChange={(e) => {
                  handlePackCountChange(group.hypothesisLabel, e.target.value);
                }}
                onBlur={() => {
                  handlePackCountBlur(group.hypothesisLabel);
                }}
                fullWidth
                size="small"
              />
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
          <Button onClick={handleCloseDialog} variant="text" color="error">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSplit}
            variant="contained"
            color="primary"
            disabled={isConfirmDisabled}
          >
            Add pack
          </Button>
        </DialogActions>
      </Dialog>
      <Tooltip
        title="Select two or more unpacked creatives to split into packs"
        placement="top"
      >
        <Box>
          <Button
            disabled={!isSplitAvailable}
            variant="outlined"
            color="primary"
            onClick={handleOpenDialog}
          >
            Split in packs
          </Button>
        </Box>
      </Tooltip>
    </>
  );
};

export default SplitInPacksButton;
